/**
 * 🏢 Corporate Accounts Service
 * Handles corporate account management endpoints
 */

import apiClient from './apiClient';

export type CorporateAccountStatus = 'pending' | 'active' | 'suspended' | 'rejected';

export interface CorporateAccount {
  id: string;
  companyName: string;
  billingEmail: string;
  registrationNumber?: string;
  taxId?: string;
  owner?: {
    userId: string;
    email: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
  };
  contactPerson: {
    name: string;
    email: string;
    phone: string;
    position?: string;
  };
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  status: CorporateAccountStatus;
  creditLimit: number;
  currentBalance: number;
  availableCredit: number;
  currency?: string; // NGN, USD, etc.
  approvedBy?: {
    id: string;
    name: string;
    email: string;
  };
  approvedAt?: string;
  rejectedBy?: {
    id: string;
    name: string;
    email: string;
  };
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  lastBookingAt?: string;
  totalBookings?: number;
  notes?: string;
  isPartnership?: boolean; // true if corporate account has been converted to partnership
  partnershipType?: string; // 'hotel', 'event_planner', 'promoter'
  referralCode?: string; // Partnership referral code if activated
}

export interface CorporateAccountFilters {
  page?: number;
  limit?: number;
  status?: CorporateAccountStatus | 'all';
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isPartnership?: boolean; // Filter by partnership status
}

export interface CorporateAccountApprovalRequest {
  accountId: string;
  creditLimit: number;
  notes?: string;
}

export interface CorporateAccountRejectionRequest {
  accountId: string;
  reason: string;
}

export interface CorporateAccountRegistrationRequest {
  companyName: string;
  billingEmail: string; // Required by backend
  registrationNumber?: string;
  taxId?: string;
  contactPerson: {
    name: string;
    email: string;
    phone: string;
    position?: string;
  };
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  creditLimit: number;
  ownerUserId?: string; // Required for admin registration
  notes?: string;
}

export interface UpdateCreditLimitRequest {
  creditLimit: number;
  reason?: string;
}

export interface SuspendCorporateAccountRequest {
  accountId: string;
  reason?: string;
}

export interface ResumeCorporateAccountRequest {
  accountId: string;
  notes?: string;
}

export interface DeleteCorporateAccountResponse {
  success: boolean;
  error?: string;
  data?: {
    bookingCount?: number;
    suggestion?: string;
  };
}

// Mock data for development

// Environment flag to use mock data
const USE_MOCK_DATA = false; // Backend APIs are now available

export const corporateAccountsService = {
  /**
   * Get all corporate accounts with filtering and pagination
   */
  async getCorporateAccounts(filters?: CorporateAccountFilters): Promise<{
    success: boolean;
    data?: {
      accounts: CorporateAccount[];
      pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
      };
    };
    error?: string;
  }> {
    try {
      if (USE_MOCK_DATA) {
        // Mock data filtering
        let filtered = [...mockCorporateAccounts];

        if (filters?.status && filters.status !== 'all') {
          filtered = filtered.filter(acc => acc.status === filters.status);
        }

        if (filters?.search) {
          const searchLower = filters.search.toLowerCase();
          filtered = filtered.filter(acc =>
            acc.companyName.toLowerCase().includes(searchLower) ||
            acc.contactPerson.email.toLowerCase().includes(searchLower) ||
            acc.contactPerson.name.toLowerCase().includes(searchLower)
          );
        }

        const page = filters?.page || 1;
        const limit = filters?.limit || 10;
        const start = (page - 1) * limit;
        const end = start + limit;
        const paginated = filtered.slice(start, end);

        return {
          success: true,
          data: {
            accounts: paginated,
            pagination: {
              page,
              limit,
              totalCount: filtered.length,
              totalPages: Math.ceil(filtered.length / limit),
            },
          },
        };
      }

      // Real API call
      const params = new URLSearchParams();
      // Convert page-based pagination to offset-based
      const page = filters?.page || 1;
      const limit = filters?.limit || 10;
      const offset = (page - 1) * limit;

      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      // Map frontend status to backend status
      if (filters?.status && filters.status !== 'all') {
        const backendStatus = filters.status === 'pending' ? 'pending_approval' : filters.status;
        params.append('status', backendStatus);
      }
      if (filters?.search) params.append('search', filters.search);
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) params.append('order', filters.sortOrder);
      // Filter by partnership status
      if (filters?.isPartnership !== undefined) {
        params.append('is_partnership', filters.isPartnership.toString());
      }

      const response = await apiClient.get(`/api/v1/admin/corporate?${params.toString()}`);

      console.log('📦 Corporate accounts API response:', response.data);
      console.log('📦 Backend data structure:', response.data.data);
      console.log('📦 Pagination object:', response.data.data?.pagination);
      console.log('📦 First account owner object:', response.data.data?.accounts?.[0]?.owner);
      console.log('📦 First account contactPerson:', response.data.data?.accounts?.[0]?.contactPerson);
      console.log('📦 First account partnership fields:', {
        is_partnership: response.data.data?.accounts?.[0]?.is_partnership,
        isPartnership: response.data.data?.accounts?.[0]?.isPartnership,
        partnership_type: response.data.data?.accounts?.[0]?.partnership_type,
        referral_code: response.data.data?.accounts?.[0]?.referral_code,
      });

      if (response.data.success) {
        // Transform backend response to match frontend interface
        const transformedData = {
          accounts: response.data.data.accounts?.map((account: any) => ({
            ...account,
            // Map backend status to frontend status
            status: account.status === 'pending_approval' ? 'pending' : account.status,
            // Calculate availableCredit if missing
            availableCredit: account.availableCredit ?? (account.creditLimit - account.currentBalance),
            // Construct billingAddress object from backend fields
            billingAddress: account.billingAddress && typeof account.billingAddress === 'object'
              ? account.billingAddress // Already an object
              : {
                  street: account.billingAddress || '',
                  city: account.city || '',
                  state: account.state || '',
                  zipCode: account.postalCode || '',
                  country: account.country || 'Nigeria',
                },
            // Preserve owner object for backend reference
            owner: account.owner ? {
              userId: account.owner.userId,
              email: account.owner.email,
              phone: account.owner.phone || undefined,
              firstName: account.owner.firstName || undefined,
              lastName: account.owner.lastName || undefined,
            } : undefined,
            // Always construct contactPerson from owner object
            contactPerson: {
              name: account.owner
                ? `${account.owner.firstName || ''} ${account.owner.lastName || ''}`.trim() || 'N/A'
                : 'N/A',
              email: account.owner?.email || account.billingEmail || 'N/A',
              phone: account.owner?.phone || 'N/A',
            },
            // Map backend fields
            registrationNumber: account.companyRegistrationNumber,
            // Map partnership fields
            isPartnership: account.is_partnership || account.isPartnership || false,
            partnershipType: account.partnership_type || account.partnershipType,
            referralCode: account.referral_code || account.referralCode,
            customCommissionRate: account.custom_commission_rate || account.customCommissionRate,
          })) || [],
          // Map backend pagination format to frontend format
          pagination: {
            totalCount: response.data.data?.pagination?.total ?? response.data.data?.accounts?.length ?? 0,
            offset: response.data.data?.pagination?.offset ?? 0,
            limit: response.data.data?.pagination?.limit ?? 10,
            hasMore: response.data.data?.pagination?.hasMore ?? false,
          },
        };

        console.log('📦 Transformed data:', transformedData);
        console.log('📦 Transformed first account contactPerson:', transformedData.accounts?.[0]?.contactPerson);
        console.log('📦 Transformed first account partnership:', {
          isPartnership: transformedData.accounts?.[0]?.isPartnership,
          partnershipType: transformedData.accounts?.[0]?.partnershipType,
          referralCode: transformedData.accounts?.[0]?.referralCode,
        });

        return {
          success: true,
          data: transformedData,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to fetch corporate accounts',
        };
      }
    } catch (error: any) {
      console.error('Error fetching corporate accounts:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch corporate accounts',
      };
    }
  },

  /**
   * Get single corporate account by ID
   */
  async getCorporateAccount(id: string): Promise<{
    success: boolean;
    data?: CorporateAccount;
    error?: string;
  }> {
    try {
      if (USE_MOCK_DATA) {
        const account = mockCorporateAccounts.find(acc => acc.id === id);
        if (account) {
          return { success: true, data: account };
        } else {
          return { success: false, error: 'Account not found' };
        }
      }

      const response = await apiClient.get(`/api/v1/admin/corporate/${id}`);

      if (response.data.success) {
        const account = response.data.data;
        // Transform backend response to match frontend interface
        const transformedAccount = {
          ...account,
          // Map backend status to frontend status
          status: account.status === 'pending_approval' ? 'pending' : account.status,
          availableCredit: account.availableCredit ?? (account.creditLimit - account.currentBalance),
          // Construct billingAddress object from backend fields
          billingAddress: account.billingAddress && typeof account.billingAddress === 'object'
            ? account.billingAddress // Already an object
            : {
                street: account.billingAddress || '',
                city: account.city || '',
                state: account.state || '',
                zipCode: account.postalCode || '',
                country: account.country || 'Nigeria',
              },
          // Preserve owner object for backend reference
          owner: account.owner ? {
            userId: account.owner.userId,
            email: account.owner.email,
            phone: account.owner.phone || undefined,
            firstName: account.owner.firstName || undefined,
            lastName: account.owner.lastName || undefined,
          } : undefined,
          // Always construct contactPerson from owner object
          contactPerson: {
            name: account.owner
              ? `${account.owner.firstName || ''} ${account.owner.lastName || ''}`.trim() || 'N/A'
              : 'N/A',
            email: account.owner?.email || account.billingEmail || 'N/A',
            phone: account.owner?.phone || 'N/A',
          },
          registrationNumber: account.companyRegistrationNumber,
        };

        return {
          success: true,
          data: transformedAccount,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to fetch corporate account',
        };
      }
    } catch (error: any) {
      console.error('Error fetching corporate account:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch corporate account',
      };
    }
  },

  /**
   * Approve a pending corporate account
   */
  async approveCorporateAccount(request: CorporateAccountApprovalRequest): Promise<{
    success: boolean;
    data?: CorporateAccount;
    error?: string;
  }> {
    try {
      if (USE_MOCK_DATA) {
        // Mock approval - just return success
        const account = mockCorporateAccounts.find(acc => acc.id === request.accountId);
        if (account) {
          account.status = 'active';
          account.creditLimit = request.creditLimit;
          account.availableCredit = request.creditLimit;
          account.approvedAt = new Date().toISOString();
          account.approvedBy = {
            id: 'mock-admin',
            name: 'Mock Admin',
            email: 'admin@polarride.com',
          };
          return { success: true, data: account };
        }
        return { success: false, error: 'Account not found' };
      }

      const response = await apiClient.post(
        `/api/v1/admin/corporate/${request.accountId}/approve`,
        {
          creditLimit: request.creditLimit,
          notes: request.notes,
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to approve corporate account',
        };
      }
    } catch (error: any) {
      console.error('Error approving corporate account:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to approve corporate account',
      };
    }
  },

  /**
   * Reject a pending corporate account
   */
  async rejectCorporateAccount(request: CorporateAccountRejectionRequest): Promise<{
    success: boolean;
    data?: CorporateAccount;
    error?: string;
  }> {
    try {
      if (USE_MOCK_DATA) {
        // Mock rejection
        const account = mockCorporateAccounts.find(acc => acc.id === request.accountId);
        if (account) {
          account.status = 'rejected';
          account.rejectionReason = request.reason;
          account.rejectedAt = new Date().toISOString();
          account.rejectedBy = {
            id: 'mock-admin',
            name: 'Mock Admin',
            email: 'admin@polarride.com',
          };
          return { success: true, data: account };
        }
        return { success: false, error: 'Account not found' };
      }

      const response = await apiClient.post(
        `/api/v1/admin/corporate/${request.accountId}/reject`,
        {
          reason: request.reason,
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to reject corporate account',
        };
      }
    } catch (error: any) {
      console.error('Error rejecting corporate account:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reject corporate account',
      };
    }
  },

  /**
   * Register new corporate account (admin direct registration)
   */
  async registerCorporateAccount(request: CorporateAccountRegistrationRequest): Promise<{
    success: boolean;
    data?: CorporateAccount;
    error?: string;
  }> {
    try {
      if (USE_MOCK_DATA) {
        // Mock registration
        const newAccount: CorporateAccount = {
          id: `corp-${Date.now()}`,
          ...request,
          status: 'active',
          currentBalance: 0,
          availableCredit: request.creditLimit,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          approvedAt: new Date().toISOString(),
          approvedBy: {
            id: 'mock-admin',
            name: 'Mock Admin',
            email: 'admin@polarride.com',
          },
          totalBookings: 0,
        };
        mockCorporateAccounts.push(newAccount);
        return { success: true, data: newAccount };
      }

      // Transform frontend request structure to backend format
      const backendRequest = {
        companyName: request.companyName,
        companyRegistrationNumber: request.registrationNumber,
        taxId: request.taxId,
        billingEmail: request.billingEmail,
        billingPhone: request.contactPerson?.phone,
        billingAddress: request.billingAddress?.street,  // Backend expects just the street
        city: request.billingAddress?.city,
        state: request.billingAddress?.state,
        country: request.billingAddress?.country,
        postalCode: request.billingAddress?.zipCode,
        ownerUserId: request.ownerUserId,  // Required by backend
        creditLimit: request.creditLimit,
        notes: request.notes,
      };

      console.log('📤 Sending registration request:', backendRequest);
      const response = await apiClient.post('/api/v1/admin/corporate/register', backendRequest);
      console.log('📥 Registration response:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to register corporate account',
        };
      }
    } catch (error: any) {
      console.error('Error registering corporate account:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to register corporate account',
      };
    }
  },

  /**
   * Update credit limit for an account
   */
  async updateCreditLimit(accountId: string, request: UpdateCreditLimitRequest): Promise<{
    success: boolean;
    data?: CorporateAccount;
    error?: string;
  }> {
    try {
      if (USE_MOCK_DATA) {
        const account = mockCorporateAccounts.find(acc => acc.id === accountId);
        if (account) {
          const diff = request.creditLimit - account.creditLimit;
          account.creditLimit = request.creditLimit;
          account.availableCredit += diff;
          account.updatedAt = new Date().toISOString();
          return { success: true, data: account };
        }
        return { success: false, error: 'Account not found' };
      }

      const response = await apiClient.patch(
        `/api/v1/admin/corporate/${accountId}`,
        { creditLimit: request.creditLimit, notes: request.notes }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to update credit limit',
        };
      }
    } catch (error: any) {
      console.error('Error updating credit limit:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update credit limit',
      };
    }
  },

  /**
   * Suspend a corporate account
   */
  async suspendCorporateAccount(request: SuspendCorporateAccountRequest): Promise<{
    success: boolean;
    data?: CorporateAccount;
    error?: string;
  }> {
    try {
      if (USE_MOCK_DATA) {
        const account = mockCorporateAccounts.find(acc => acc.id === request.accountId);
        if (account) {
          account.status = 'suspended';
          account.updatedAt = new Date().toISOString();
          return { success: true, data: account };
        }
        return { success: false, error: 'Account not found' };
      }

      const response = await apiClient.post(
        `/api/v1/admin/corporate/${request.accountId}/suspend`,
        { reason: request.reason }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to suspend corporate account',
        };
      }
    } catch (error: any) {
      console.error('Error suspending corporate account:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to suspend corporate account',
      };
    }
  },

  /**
   * Resume/Reactivate a suspended corporate account
   */
  async resumeCorporateAccount(request: ResumeCorporateAccountRequest): Promise<{
    success: boolean;
    data?: CorporateAccount;
    error?: string;
  }> {
    try {
      if (USE_MOCK_DATA) {
        const account = mockCorporateAccounts.find(acc => acc.id === request.accountId);
        if (account) {
          account.status = 'active';
          account.updatedAt = new Date().toISOString();
          return { success: true, data: account };
        }
        return { success: false, error: 'Account not found' };
      }

      const response = await apiClient.post(
        `/api/v1/admin/corporate/${request.accountId}/resume`,
        { notes: request.notes }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to resume corporate account',
        };
      }
    } catch (error: any) {
      console.error('Error resuming corporate account:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resume corporate account',
      };
    }
  },

  /**
   * Delete a corporate account
   */
  async deleteCorporateAccount(accountId: string): Promise<DeleteCorporateAccountResponse> {
    try {
      if (USE_MOCK_DATA) {
        const index = mockCorporateAccounts.findIndex(acc => acc.id === accountId);
        if (index !== -1) {
          mockCorporateAccounts.splice(index, 1);
          return { success: true };
        }
        return { success: false, error: 'Account not found' };
      }

      const response = await apiClient.delete(`/api/v1/admin/corporate/${accountId}`);

      if (response.data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to delete corporate account',
          data: response.data.data,
        };
      }
    } catch (error: any) {
      console.error('Error deleting corporate account:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete corporate account',
        data: error.response?.data?.data,
      };
    }
  },
};
